/* eslint-disable react-refresh/only-export-components */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";

// ====================== ENHANCED INTERFACES ======================

// Application interfaces
interface Application {
  id: string;
  jobId: string;
  userId: string;
  status: "pending" | "reviewed" | "accepted" | "rejected" | "withdrawn";
  coverLetter?: string;
  resumeUrl?: string;
  createdAt: string;
  updatedAt: string;
  job: {
    id: string;
    title: string;
    company: string;
    location: string;
    salary: string;
    type: string;
    category: string;
    deadline?: string;
    createdAt: string;
    postedBy: {
      id: string;
      name: string;
      email: string;
    };
  };
  user: {
    id: string;
    name: string;
    email: string;
    profile?: {
      phone?: string;
      location?: string;
    };
  };
}

// User interface for admin management
interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  subscribed: boolean;
  active?: boolean;
  deactivatedAt?: string;
  deactivationType?: string;
  createdAt?: string;
  profile?: {
    phone?: string;
    location?: string;
  };
  firstName?: string;
  lastName?: string;
}

// Job interface for admin management
interface Job {
  deadline: string;
  id: string;
  title: string;
  company: string;
  location: string;
  salary: string;
  type: string;
  createdAt: string;
  imageUrl?: string;
  _count?: {
    jobApplications: number;
  };
}

// Job Alert interface
interface JobAlert {
  id: string;
  name: string;
  category?: string;
  jobType?: string;
  location?: string;
  keywords: string[];
  frequency: "instant" | "daily" | "weekly";
  isActive: boolean;
  lastTriggered?: string;
  triggerCount: number;
  userId: string;
  user: {
    id: string;
    name: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

// Package interfaces
export interface Package {
  id: string;
  name: string;
  code: string;
  description?: string;
  monthlyPrice: number;
  annualPrice: number;
  originalMonthlyPrice?: number;
  originalAnnualPrice?: number;
  monthlyDuration: number;
  annualDuration: number;
  isPopular: boolean;
  isActive: boolean;
  orderIndex: number;
  features: PackageFeature[];
  type: "whitelist_request" | "job_seeker";
  maxJobs?: number;
  durationDays?: number;
  deletedAt?: string;
}

export interface PackageFeature {
  id: string;
  text: string;
  orderIndex: number;
}

interface PackageStats {
  totalPackages: number;
  activePackages: number;
  popularPackages: number;
  totalSubscriptions: number;
  packageSubscriptions: Array<{
    packageId: string;
    _count: { _all: number };
  }>;
  packageTypes: {
    whitelist_request: number;
    job_seeker: number;
  };
}

//contact and whatsapp interface

interface MessagingResult {
  success: boolean;
  messageId?: string;
  error?: string;
  platform: "email" | "whatsapp";
  sentAt?: string;
}

interface ContactOptions {
  sendEmail?: boolean;
  sendWhatsApp?: boolean;
  platform?: "email" | "whatsapp" | "both";
}

interface ContactData {
  subject: string;
  message: string;
  applicantEmail?: string;
  applicantName?: string;
  applicantPhone?: string; // Add for WhatsApp
  jobTitle?: string;
  companyName?: string;
  currentUserName?: string;
  options?: ContactOptions;
}

interface WhatsAppStatus {
  connected: boolean;
  qrCode?: string;
  clientState?: string;
  phoneNumber?: string;
  lastActive?: string;
}
// Contact and Interview interfaces
interface ContactData {
  subject: string;
  message: string;
  sendEmail: boolean;
  applicantEmail?: string;
  applicantName?: string;
  jobTitle?: string;
  companyName?: string;
  currentUserName?: string;
}

interface InterviewData {
  date: string;
  type: string;
  location?: string;
  meetingLink?: string;
  notes?: string;
  sendEmail?: boolean;
}

// Dashboard Stats interface
interface DashboardStats {
  totalUsers: number;
  totalJobs: number;
  totalApplications: number;
  totalAlerts: number;
  pendingApplications: number;
  activeUsers: number;
  expiredJobs: number;
  activeAlerts: number;
  expiringSoonJobs: number;
}

// ====================== CONTEXT TYPE ======================

interface ApplicationContextType {
  // Application management state (user)
  applications: Application[];
  loading: boolean;
  error: string;

  // Application management actions (user)
  refreshApplications: () => Promise<void>;
  addApplication: (application: Application) => void;
  updateApplication: (id: string, updates: Partial<Application>) => void;
  removeApplication: (id: string) => void;
  hasAppliedToJob: (jobId: string) => boolean;
  getApplicationByJobId: (jobId: string) => Application | undefined;
  getApplicationsByStatus: (status: Application["status"]) => Application[];
  submitApplication: (applicationData: {
    jobId: string;
    coverLetter?: string;
    resumeUrl?: string;
  }) => Promise<{
    success: boolean;
    application?: Application;
    error?: string;
  }>;
  withdrawApplication: (
    applicationId: string,
  ) => Promise<{ success: boolean; error?: string }>;
  updateApplicationStatus: (
    applicationId: string,
    status: Application["status"],
  ) => Promise<{ success: boolean; error?: string }>;

  // Package management state
  packages: Package[];
  packageStats: PackageStats | null;
  packageLoading: boolean;
  packageError: string | null;
  packageSuccessMessage: string | null;

  // Package management actions
  refreshPackages: () => Promise<void>;
  refreshPackageStats: () => Promise<void>;
  createPackage: (
    pkgData: Omit<Package, "id">,
  ) => Promise<{ success: boolean; error?: string }>;
  updatePackage: (
    pkg: Package,
  ) => Promise<{ success: boolean; error?: string }>;
  deletePackage: (
    packageId: string,
  ) => Promise<{ success: boolean; error?: string }>;
  deactivatePackage: (
    packageId: string,
    force?: boolean,
  ) => Promise<{
    success: boolean;
    error?: string;
    requiresForce?: boolean;
    activeSubscriptions?: number;
  }>;
  togglePackageStatus: (
    packageId: string,
    isActive: boolean,
  ) => Promise<{ success: boolean; error?: string }>;
  togglePopularStatus: (
    packageId: string,
    isPopular: boolean,
  ) => Promise<{ success: boolean; error?: string }>;
  restorePackage: (
    packageId: string,
  ) => Promise<{ success: boolean; error?: string }>;

  // Package utility functions
  getSubscriptionCount: (packageId: string) => number;

  // ========== ADMIN MANAGEMENT FUNCTIONS ==========

  // Application management (admin)
  fetchAllApplications: () => Promise<Application[]>;
  deleteApplication: (
    applicationId: string,
  ) => Promise<{ success: boolean; error?: string }>;
  deleteMultipleApplications: (
    applicationIds: string[],
  ) => Promise<{ success: boolean; error?: string }>;

  // Contact and interview functionality
  // contactApplicant: (
  //   applicationId: string,
  //   contactData: ContactData,
  // ) => Promise<{ success: boolean; error?: string }>;
  scheduleInterview: (
    applicationId: string,
    interviewData: InterviewData,
  ) => Promise<{
    success: boolean;
    emailSent?: boolean;
    emailError?: string;
    error?: string;
  }>;

  // Job alerts management
  triggerJobAlerts: (
    jobId: string,
  ) => Promise<{ success: boolean; sent?: number; error?: string }>;
  triggerBulkJobAlerts: () => Promise<{
    success: boolean;
    totalSent?: number;
    totalFailed?: number;
    error?: string;
  }>;

  // Job management
  fetchJobs: () => Promise<{ jobs: Job[] }>;
  deleteJob: (jobId: string) => Promise<{ success: boolean; error?: string }>;

  // User management
  fetchUsers: () => Promise<User[]>;
  updateUserRole: (
    userId: string,
    newRole: string,
  ) => Promise<{ success: boolean; error?: string }>;
  activateUser: (
    userId: string,
  ) => Promise<{ success: boolean; error?: string }>;
  deactivateUser: (
    userId: string,
  ) => Promise<{ success: boolean; error?: string }>;
  deleteUser: (userId: string) => Promise<{ success: boolean; error?: string }>;

  // Job alerts management
  fetchJobAlerts: () => Promise<{ alerts: JobAlert[] }>;
  updateJobAlertStatus: (
    alertId: string,
    isActive: boolean,
  ) => Promise<{ success: boolean; error?: string }>;
  deleteJobAlert: (
    alertId: string,
  ) => Promise<{ success: boolean; error?: string }>;

  // Dashboard stats
  fetchApplicationStats: () => Promise<any>;
  calculateDashboardStats: (
    users: User[],
    jobs: Job[],
    applications: Application[],
    alerts: JobAlert[],
  ) => DashboardStats;

  // WhatsApp service state
  whatsappStatus: WhatsAppStatus | null;
  whatsappLoading: boolean;
  whatsappError: string | null;

  // Unified contact functionality
  contactApplicant: (
    applicationId: string,
    contactData: ContactData,
  ) => Promise<{
    success: boolean;
    results?: MessagingResult[];
    error?: string;
  }>;

  // WhatsApp specific methods
  getWhatsAppStatus: () => Promise<WhatsAppStatus>;
  sendWhatsAppMessage: (
    phoneNumber: string,
    message: string,
    options?: {
      applicantName?: string;
      jobTitle?: string;
      companyName?: string;
    },
  ) => Promise<MessagingResult>;
  sendBulkWhatsAppMessages: (
    recipients: Array<{
      phoneNumber: string;
      name?: string;
      jobTitle?: string;
      companyName?: string;
      message?: string;
    }>,
  ) => Promise<{
    success: boolean;
    totalSent: number;
    totalFailed: number;
    results: MessagingResult[];
  }>;
  disconnectWhatsApp: () => Promise<{ success: boolean; error?: string }>;
  reconnectWhatsApp: () => Promise<{ success: boolean; error?: string }>;
}

export const ApplicationContext = createContext<
  ApplicationContextType | undefined
>(undefined);

// ====================== PROVIDER COMPONENT ======================

export const ApplicationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Application state
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Package management state
  const [packages, setPackages] = useState<Package[]>([]);
  const [packageStats, setPackageStats] = useState<PackageStats | null>(null);
  const [packageLoading, setPackageLoading] = useState(false);
  const [packageError, setPackageError] = useState<string | null>(null);
  const [packageSuccessMessage, setPackageSuccessMessage] = useState<
    string | null
  >(null);

  // WhatsApp state
  const [whatsappStatus, setWhatsappStatus] = useState<WhatsAppStatus | null>(
    null,
  );
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [whatsappError, setWhatsappError] = useState<string | null>(null);

  // Use AuthContext's apiRequest and user state
  const { user, apiRequest } = useAuth();

  // Auto-hide success messages for packages
  useEffect(() => {
    if (packageSuccessMessage) {
      const timer = setTimeout(() => {
        setPackageSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [packageSuccessMessage]);

  // ========== ENHANCED APPLICATION MANAGEMENT FUNCTIONS ==========

  const fetchApplications = useCallback(async () => {
    if (!user) {
      setApplications([]);
      return;
    }

    try {
      setLoading(true);
      setError("");

      console.log("📥 Fetching user applications...");
      const data = await apiRequest<{ applications: Application[] }>(
        "/jobs/applications/my-applications",
      );

      console.log(
        "✅ Applications fetched successfully:",
        data.applications?.length || 0,
      );
      setApplications(data.applications || []);
    } catch (err: any) {
      console.error("❌ Error fetching applications:", err);
      setError(err.message || "Failed to fetch applications");
      setApplications([]);
    } finally {
      setLoading(false);
    }
  }, [user, apiRequest]);

  const addApplication = useCallback((application: Application) => {
    setApplications((prev) => {
      const exists = prev.find((app) => app.id === application.id);
      if (exists) return prev;
      return [...prev, application];
    });
  }, []);

  const updateApplication = useCallback(
    (id: string, updates: Partial<Application>) => {
      setApplications((prev) =>
        prev.map((app) =>
          app.id === id
            ? { ...app, ...updates, updatedAt: new Date().toISOString() }
            : app,
        ),
      );
    },
    [],
  );

  const removeApplication = useCallback((id: string) => {
    setApplications((prev) => prev.filter((app) => app.id !== id));
  }, []);

  const hasAppliedToJob = useCallback(
    (jobId: string): boolean => {
      return applications.some((app) => app.jobId === jobId);
    },
    [applications],
  );

  const getApplicationByJobId = useCallback(
    (jobId: string): Application | undefined => {
      return applications.find((app) => app.jobId === jobId);
    },
    [applications],
  );

  const getApplicationsByStatus = useCallback(
    (status: Application["status"]): Application[] => {
      return applications.filter((app) => app.status === status);
    },
    [applications],
  );

  const submitApplication = async (applicationData: {
    jobId: string;
    coverLetter?: string;
    resumeUrl?: string;
  }): Promise<{
    success: boolean;
    application?: Application;
    error?: string;
  }> => {
    try {
      setLoading(true);
      setError("");

      console.log("🔄 Submitting application via apiRequest:", {
        jobId: applicationData.jobId,
        userId: user?.id,
        hasToken: !!localStorage.getItem("token"),
        userEmail: user?.email,
      });

      // Prepare the request body
      const requestBody: any = {
        coverLetter: applicationData.coverLetter || "",
      };

      // Only include resumeUrl if it's provided
      if (applicationData.resumeUrl) {
        requestBody.resumeUrl = applicationData.resumeUrl;
      }

      console.log("📤 Application request body:", requestBody);

      // Use apiRequest from AuthContext with proper error handling
      const data = await apiRequest<{
        application: Application;
        message: string;
        code: string;
      }>(`/jobs/${applicationData.jobId}/apply`, {
        method: "POST",
        body: JSON.stringify(requestBody),
      });

      console.log("✅ Application submission success:", data);

      const newApplication: Application = data.application;
      addApplication(newApplication);

      // Clear any previous errors
      setError("");

      return { success: true, application: newApplication };
    } catch (err: any) {
      console.error("❌ Error submitting application:", err);

      // Enhanced error handling with specific error codes
      const errorMessage = err.message || "Failed to submit application";
      const lowerCaseMessage = errorMessage.toLowerCase();

      if (
        lowerCaseMessage.includes("already applied") ||
        err.message?.includes("ALREADY_APPLIED")
      ) {
        const specificError = "You have already applied to this job";
        setError(specificError);
        return { success: false, error: specificError };
      }
      if (
        lowerCaseMessage.includes("deadline") ||
        err.message?.includes("DEADLINE_PASSED")
      ) {
        const specificError = "Application deadline has passed";
        setError(specificError);
        return { success: false, error: specificError };
      }
      if (
        lowerCaseMessage.includes("not found") ||
        err.message?.includes("JOB_NOT_FOUND")
      ) {
        const specificError = "Job not found";
        setError(specificError);
        return { success: false, error: specificError };
      }
      if (
        lowerCaseMessage.includes("resume") ||
        err.message?.includes("RESUME_REQUIRED")
      ) {
        const specificError =
          "Resume is required for job application. Please upload a resume first.";
        setError(specificError);
        return { success: false, error: specificError };
      }
      if (
        lowerCaseMessage.includes("profile") ||
        err.message?.includes("PROFILE_REQUIRED")
      ) {
        const specificError =
          "Please complete your profile before applying to jobs";
        setError(specificError);
        return { success: false, error: specificError };
      }
      if (
        lowerCaseMessage.includes("authentication") ||
        lowerCaseMessage.includes("unauthorized") ||
        lowerCaseMessage.includes("token")
      ) {
        const specificError = "Please log in to apply for jobs";
        setError(specificError);
        return { success: false, error: specificError };
      }

      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const withdrawApplication = async (
    applicationId: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      setLoading(true);
      setError("");

      console.log("🗑️ Withdrawing application:", applicationId);

      await apiRequest(`/jobs/applications/${applicationId}/withdraw`, {
        method: "DELETE",
      });

      updateApplication(applicationId, {
        status: "withdrawn",
        updatedAt: new Date().toISOString(),
      });

      console.log("✅ Application withdrawn successfully");
      return { success: true };
    } catch (err: any) {
      console.error("❌ Error withdrawing application:", err);

      // Enhanced error handling
      const errorMessage = err.message || "Failed to withdraw application";
      const lowerCaseMessage = errorMessage.toLowerCase();

      if (
        lowerCaseMessage.includes("not found") ||
        err.message?.includes("APPLICATION_NOT_FOUND")
      ) {
        setError("Application not found");
        return { success: false, error: "Application not found" };
      }
      if (
        lowerCaseMessage.includes("already withdrawn") ||
        err.message?.includes("ALREADY_WITHDRAWN")
      ) {
        setError("Application already withdrawn");
        return { success: false, error: "Application already withdrawn" };
      }
      if (
        lowerCaseMessage.includes("cannot withdraw") ||
        err.message?.includes("CANNOT_WITHDRAW_ACCEPTED")
      ) {
        setError("Cannot withdraw an accepted application");
        return {
          success: false,
          error: "Cannot withdraw an accepted application",
        };
      }
      if (
        lowerCaseMessage.includes("already rejected") ||
        err.message?.includes("ALREADY_REJECTED")
      ) {
        setError("Application already rejected");
        return { success: false, error: "Application already rejected" };
      }
      if (
        lowerCaseMessage.includes("not authorized") ||
        err.message?.includes("WITHDRAW_NOT_AUTHORIZED")
      ) {
        setError("Not authorized to withdraw this application");
        return {
          success: false,
          error: "Not authorized to withdraw this application",
        };
      }

      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const updateApplicationStatusUser = async (
    applicationId: string,
    status: Application["status"],
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      setLoading(true);
      setError("");

      console.log("📝 Updating application status:", { applicationId, status });

      const data = await apiRequest<{ application: Application }>(
        `/admin/applications/${applicationId}`,
        {
          method: "PATCH",
          body: JSON.stringify({ status }),
        },
      );

      const updatedApplication: Application = data.application;
      updateApplication(applicationId, updatedApplication);

      console.log("✅ Application status updated successfully");
      return { success: true };
    } catch (err: any) {
      console.error("❌ Error updating application status:", err);

      // Enhanced error handling
      const errorMessage = err.message || "Failed to update application status";
      const lowerCaseMessage = errorMessage.toLowerCase();

      if (
        lowerCaseMessage.includes("not found") ||
        err.message?.includes("APPLICATION_NOT_FOUND")
      ) {
        setError("Application not found");
        return { success: false, error: "Application not found" };
      }
      if (
        lowerCaseMessage.includes("invalid status") ||
        err.message?.includes("INVALID_STATUS")
      ) {
        setError("Invalid application status");
        return { success: false, error: "Invalid application status" };
      }

      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // ========== ENHANCED PACKAGE MANAGEMENT FUNCTIONS ==========

  // In your usePackageManagement hook or ApplicationContext, update the fetchPackages function:

  const fetchPackages = useCallback(async () => {
    if (!user || user.role !== "admin") return;

    try {
      setPackageLoading(true);
      setPackageError(null);

      console.log(
        "📦 Fetching packages with includeInactive=true&includeDeleted=true...",
      );

      // Add retry logic for initial fetch
      let retries = 3;
      let lastError = null;

      while (retries > 0) {
        try {
          const data = await apiRequest<any>(
            "/subscriptions/admin/packages?includeInactive=true&includeDeleted=true",
          );

          console.log("🔍 RAW API RESPONSE:", data);

          // Check if we're getting deactivated packages
          if (Array.isArray(data)) {
            const activePackages = data.filter((pkg) => pkg.isActive);
            const inactivePackages = data.filter((pkg) => !pkg.isActive);
            console.log(
              `📊 Package Stats: Total: ${data.length}, Active: ${activePackages.length}, Inactive: ${inactivePackages.length}`,
            );
            setPackages(data);
            break; // Success, break out of retry loop
          } else if (data && Array.isArray(data.packages)) {
            const activePackages = data.packages.filter(
              (pkg: { isActive: any }) => pkg.isActive,
            );
            const inactivePackages = data.packages.filter(
              (pkg: { isActive: any }) => !pkg.isActive,
            );
            console.log(
              `📊 Package Stats: Total: ${data.packages.length}, Active: ${activePackages.length}, Inactive: ${inactivePackages.length}`,
            );
            setPackages(data.packages);
            break; // Success, break out of retry loop
          } else {
            console.warn("❌ Unexpected packages response format:", data);
            setPackages([]);
            break;
          }
        } catch (err: any) {
          lastError = err;
          retries--;
          console.warn(
            `❌ Package fetch failed, ${retries} retries left:`,
            err,
          );

          if (retries > 0) {
            // Wait before retrying (exponential backoff)
            await new Promise((resolve) =>
              setTimeout(resolve, 1000 * (4 - retries)),
            );
          }
        }
      }

      if (retries === 0 && lastError) {
        throw lastError;
      }
    } catch (err: any) {
      console.error("❌ Error fetching packages after retries:", err);
      setPackageError(
        err.message || "An error occurred while fetching packages",
      );
      setPackages([]);
    } finally {
      setPackageLoading(false);
    }
  }, [user, apiRequest]);

  const fetchPackageStats = useCallback(async () => {
    if (!user || user.role !== "admin") return;

    try {
      console.log("📊 Fetching package stats...");
      const data = await apiRequest<PackageStats>(
        "/subscriptions/admin/packages/stats",
      );

      console.log("✅ Package stats fetched successfully");
      setPackageStats(data);
    } catch (err) {
      console.error("❌ Failed to fetch package stats:", err);
    }
  }, [user, apiRequest]);

  const createPackage = async (
    pkgData: Omit<Package, "id">,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log("🆕 Creating package:", pkgData.name);

      await apiRequest("/subscriptions/admin/packages", {
        method: "POST",
        body: JSON.stringify(pkgData),
      });

      await fetchPackages();
      await fetchPackageStats();
      setPackageSuccessMessage("Package created successfully!");
      setPackageError(null);

      console.log("✅ Package created successfully");
      return { success: true };
    } catch (err: any) {
      const errorMessage = err.message || "Failed to create package";
      console.error("❌ Error creating package:", errorMessage);
      setPackageError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const updatePackage = async (
    pkg: Package,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log("✏️ Updating package:", pkg.name);

      await apiRequest(`/subscriptions/admin/packages/${pkg.id}`, {
        method: "PUT",
        body: JSON.stringify(pkg),
      });

      await fetchPackages();
      setPackageSuccessMessage("Package updated successfully!");
      setPackageError(null);

      console.log("✅ Package updated successfully");
      return { success: true };
    } catch (err: any) {
      const errorMessage = err.message || "Failed to update package";
      console.error("❌ Error updating package:", errorMessage);
      setPackageError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const deletePackage = async (
    packageId: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log("🗑️ Deleting package:", packageId);

      await apiRequest(`/subscriptions/admin/packages/${packageId}`, {
        method: "DELETE",
      });

      await fetchPackages();
      await fetchPackageStats();
      setPackageSuccessMessage("Package deleted successfully!");
      setPackageError(null);

      console.log("✅ Package deleted successfully");
      return { success: true };
    } catch (err: any) {
      const errorMessage = err.message || "Failed to delete package";
      console.error("❌ Error deleting package:", errorMessage);
      setPackageError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // FIXED: Proper deactivatePackage function implementation
  const deactivatePackage = async (
    packageId: string,
    force: boolean = false,
  ): Promise<{
    success: boolean;
    error?: string;
    requiresForce?: boolean;
    activeSubscriptions?: number;
  }> => {
    try {
      console.log("🔧 Deactivating package:", { packageId, force });

      const response = await apiRequest<{
        message: string;
        code: string;
        warning?: string;
        activeSubscriptions?: number;
      }>(`/subscriptions/admin/packages/${packageId}/deactivate`, {
        method: "Post",
        body: JSON.stringify({ force }),
      });

      await fetchPackages();

      let successMessage = "Package deactivated successfully";
      if (response.warning) {
        successMessage += ` - ${response.warning}`;
      }

      setPackageSuccessMessage(successMessage);
      setPackageError(null);

      console.log("✅ Package deactivated successfully");
      return { success: true };
    } catch (err: any) {
      const errorMessage = err.message || "Failed to deactivate package";
      console.error("❌ Error deactivating package:", errorMessage);
      setPackageError(errorMessage);

      // Check if it's an active subscriptions error
      if (
        err.message?.includes("PACKAGE_HAS_ACTIVE_SUBSCRIPTIONS") ||
        errorMessage.includes("active subscriptions")
      ) {
        const activeSubscriptionsMatch = errorMessage.match(/\d+/);
        const activeSubscriptions = activeSubscriptionsMatch
          ? parseInt(activeSubscriptionsMatch[0])
          : 0;

        return {
          success: false,
          error: `Cannot deactivate package with ${activeSubscriptions} active subscription${activeSubscriptions !== 1 ? "s" : ""}.`,
          activeSubscriptions,
          requiresForce: true,
        };
      }

      return { success: false, error: errorMessage };
    }
  };

  const togglePackageStatus = async (
    packageId: string,
    isActive: boolean,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log("🔧 Toggling package status:", { packageId, isActive });

      await apiRequest(`/subscriptions/admin/packages/${packageId}/status`, {
        method: "PATCH",
        body: JSON.stringify({ isActive }),
      });

      await fetchPackages();
      setPackageSuccessMessage(
        `Package ${isActive ? "activated" : "deactivated"} successfully!`,
      );
      setPackageError(null);

      console.log("✅ Package status toggled successfully");
      return { success: true };
    } catch (err: any) {
      const errorMessage = err.message || "Failed to update package status";
      console.error("❌ Error toggling package status:", errorMessage);
      setPackageError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const togglePopularStatus = async (
    packageId: string,
    isPopular: boolean,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log("⭐ Toggling package popularity:", { packageId, isPopular });

      await apiRequest(`/subscriptions/admin/packages/${packageId}/popular`, {
        method: "PATCH",
        body: JSON.stringify({ isPopular }),
      });

      await fetchPackages();
      setPackageSuccessMessage(
        `Package ${isPopular ? "marked as popular" : "unmarked as popular"}!`,
      );
      setPackageError(null);

      console.log("✅ Package popularity toggled successfully");
      return { success: true };
    } catch (err: any) {
      const errorMessage = err.message || "Failed to update package popularity";
      console.error("❌ Error toggling package popularity:", errorMessage);
      setPackageError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const restorePackage = async (
    packageId: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log("🔄 Restoring package:", packageId);

      const response = await apiRequest<{
        message: string;
        code: string;
        package: Package;
        note?: string;
      }>(`/subscriptions/admin/packages/${packageId}/restore`, {
        method: "POST", // Make sure this matches your backend
      });

      console.log("✅ Package restore API response:", response);

      // Refresh data to ensure consistency
      await fetchPackages();
      await fetchPackageStats();

      const successMsg = response.note
        ? `Package restored successfully! ${response.note}`
        : "Package restored successfully!";

      setPackageSuccessMessage(successMsg);
      setPackageError(null);

      console.log("✅ Package restored successfully in frontend");
      return { success: true };
    } catch (err: any) {
      const errorMessage = err.message || "Failed to restore package";
      console.error("❌ Error restoring package:", errorMessage);
      setPackageError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const getSubscriptionCount = useCallback(
    (packageId: string): number => {
      if (!packageStats?.packageSubscriptions) return 0;
      const packageStatsItem = packageStats.packageSubscriptions.find(
        (p) => p.packageId === packageId,
      );
      return packageStatsItem?._count._all || 0;
    },
    [packageStats],
  );

  // ========== ADMIN MANAGEMENT FUNCTIONS ==========

  // Application management (admin)
  const fetchAllApplications = useCallback(async (): Promise<Application[]> => {
    try {
      console.log("📋 Fetching all applications (admin)...");
      const data = await apiRequest<any>("/admin/applications");
      console.log(
        "✅ All applications fetched:",
        data.applications?.length || data.length,
      );

      if (Array.isArray(data)) {
        return data;
      } else if (data.applications && Array.isArray(data.applications)) {
        return data.applications;
      } else {
        console.warn("Unexpected applications response format:", data);
        return [];
      }
    } catch (err: any) {
      console.error("❌ Error fetching all applications:", err);
      throw new Error(err.message || "Failed to fetch applications");
    }
  }, [apiRequest]);

  const deleteApplication = async (
    applicationId: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log("🗑️ Deleting application:", applicationId);
      await apiRequest(`/admin/applications/${applicationId}`, {
        method: "DELETE",
      });
      console.log("✅ Application deleted successfully");
      return { success: true };
    } catch (err: any) {
      console.error("❌ Error deleting application:", err);
      return {
        success: false,
        error: err.message || "Failed to delete application",
      };
    }
  };

  const deleteMultipleApplications = async (
    applicationIds: string[],
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log("🗑️ Deleting multiple applications:", applicationIds.length);

      const deletePromises = applicationIds.map((applicationId) =>
        apiRequest(`/admin/applications/${applicationId}`, {
          method: "DELETE",
        }),
      );

      await Promise.all(deletePromises);
      console.log("✅ Multiple applications deleted successfully");
      return { success: true };
    } catch (err: any) {
      console.error("❌ Error deleting multiple applications:", err);
      return {
        success: false,
        error: err.message || "Failed to delete applications",
      };
    }
  };

  // Contact and interview functionality
  const contactApplicant = async (
    applicationId: string,
    contactData: ContactData,
  ): Promise<{
    success: boolean;
    results?: MessagingResult[];
    error?: string;
  }> => {
    try {
      console.log("📧📱 Contacting applicant via multiple platforms:", {
        applicationId,
        platforms: contactData.options?.platform || "email",
      });

      const results: MessagingResult[] = [];
      let hasError = false;
      let errorMessage = "";

      // Prepare common data
      const messageData = {
        applicantEmail: contactData.applicantEmail,
        applicantName: contactData.applicantName,
        applicantPhone: contactData.applicantPhone,
        subject: contactData.subject,
        message: contactData.message,
        jobTitle: contactData.jobTitle,
        companyName: contactData.companyName,
        currentUserName: contactData.currentUserName || user?.name,
      };

      const platform = contactData.options?.platform || "email";

      // Send email if requested
      if (platform === "email" || platform === "both") {
        try {
          const emailResult = await apiRequest<MessagingResult>(
            "/email/contact-applicant",
            {
              method: "POST",
              body: JSON.stringify({
                ...messageData,
                sendEmail: contactData.options?.sendEmail !== false,
              }),
            },
          );

          results.push({
            ...emailResult,
            platform: "email",
            sentAt: new Date().toISOString(),
          });

          if (!emailResult.success) {
            hasError = true;
            errorMessage += `Email failed: ${emailResult.error}. `;
          }
        } catch (emailError: any) {
          hasError = true;
          results.push({
            success: false,
            error: emailError.message,
            platform: "email",
            sentAt: new Date().toISOString(),
          });
          errorMessage += `Email error: ${emailError.message}. `;
        }
      }

      // Send WhatsApp if requested and phone number available
      if (
        (platform === "whatsapp" || platform === "both") &&
        contactData.applicantPhone
      ) {
        try {
          const whatsappResult = await apiRequest<MessagingResult>(
            "/whatsapp/send-test", // Or create a dedicated endpoint
            {
              method: "POST",
              body: JSON.stringify({
                phoneNumber: contactData.applicantPhone,
                message: `Hello ${contactData.applicantName},\n\n${contactData.message}\n\nRegarding: ${contactData.jobTitle || "Your Application"}\nFrom: ${contactData.currentUserName || user?.name} at ${contactData.companyName || "Find-Work Team"}`,
              }),
            },
          );

          results.push({
            ...whatsappResult,
            platform: "whatsapp",
            sentAt: new Date().toISOString(),
          });

          if (!whatsappResult.success) {
            hasError = true;
            errorMessage += `WhatsApp failed: ${whatsappResult.error}. `;
          }
        } catch (whatsappError: any) {
          hasError = true;
          results.push({
            success: false,
            error: whatsappError.message,
            platform: "whatsapp",
            sentAt: new Date().toISOString(),
          });
          errorMessage += `WhatsApp error: ${whatsappError.message}. `;
        }
      }

      const success = !hasError && results.some((r) => r.success);

      return {
        success,
        results,
        error: hasError ? errorMessage.trim() : undefined,
      };
    } catch (err: any) {
      console.error("❌ Error in multi-platform contact:", err);
      return {
        success: false,
        error: err.message || "Failed to contact applicant",
      };
    }
  };

  // WhatsApp specific functions
  const getWhatsAppStatus = async (): Promise<WhatsAppStatus> => {
    try {
      setWhatsappLoading(true);
      setWhatsappError(null);

      const status = await apiRequest<WhatsAppStatus>("/whatsapp/status");
      setWhatsappStatus(status);

      return status;
    } catch (err: any) {
      const errorMsg = err.message || "Failed to get WhatsApp status";
      setWhatsappError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setWhatsappLoading(false);
    }
  };

  const sendWhatsAppMessage = async (
    phoneNumber: string,
    message: string,
    options?: {
      applicantName?: string;
      jobTitle?: string;
      companyName?: string;
    },
  ): Promise<MessagingResult> => {
    try {
      // Format phone number if needed
      const formattedPhone = phoneNumber.startsWith("+")
        ? phoneNumber
        : `+${phoneNumber}`;

      const result = await apiRequest<MessagingResult>("/whatsapp/send-test", {
        method: "POST",
        body: JSON.stringify({
          phoneNumber: formattedPhone,
          message,
          ...options,
        }),
      });

      return {
        ...result,
        platform: "whatsapp",
        sentAt: new Date().toISOString(),
      };
    } catch (err: any) {
      return {
        success: false,
        error: err.message || "Failed to send WhatsApp message",
        platform: "whatsapp",
      };
    }
  };

  const sendBulkWhatsAppMessages = async (
    recipients: Array<{
      phoneNumber: string;
      name?: string;
      jobTitle?: string;
      companyName?: string;
      message?: string;
    }>,
  ): Promise<{
    success: boolean;
    totalSent: number;
    totalFailed: number;
    results: MessagingResult[];
  }> => {
    try {
      const result = await apiRequest<{
        success: boolean;
        totalSent: number;
        totalFailed: number;
        results: MessagingResult[];
      }>("/whatsapp/bulk", {
        method: "POST",
        body: JSON.stringify({ recipients }),
      });

      return result;
    } catch (err: any) {
      console.error("❌ Error sending bulk WhatsApp messages:", err);
      return {
        success: false,
        totalSent: 0,
        totalFailed: recipients.length,
        results: recipients.map(() => ({
          success: false,
          error: err.message,
          platform: "whatsapp",
        })),
      };
    }
  };

  const disconnectWhatsApp = async (): Promise<{
    success: boolean;
    error?: string;
  }> => {
    try {
      await apiRequest("/whatsapp/disconnect", { method: "POST" });
      setWhatsappStatus((prev) =>
        prev ? { ...prev, connected: false } : null,
      );
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  };

  const reconnectWhatsApp = async (): Promise<{
    success: boolean;
    error?: string;
  }> => {
    try {
      await apiRequest("/whatsapp/reconnect", { method: "POST" });
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  };

  const scheduleInterview = async (
    applicationId: string,
    interviewData: InterviewData,
  ): Promise<{
    success: boolean;
    emailSent?: boolean;
    emailError?: string;
    error?: string;
  }> => {
    try {
      console.log("📅 Scheduling interview for application:", applicationId);

      const backendInterviewData = {
        date: interviewData.date,
        type: interviewData.type,
        location: interviewData.location || "",
        meetingLink: interviewData.meetingLink || "",
        notes: interviewData.notes || "",
        sendEmail: interviewData.sendEmail !== false,
      };

      const result = await apiRequest<{
        emailSent: boolean;
        emailError?: string;
      }>(`/jobs/applications/${applicationId}/schedule-interview`, {
        method: "POST",
        body: JSON.stringify(backendInterviewData),
      });

      console.log("✅ Interview scheduled successfully");
      return {
        success: true,
        emailSent: result.emailSent,
        emailError: result.emailError,
      };
    } catch (err: any) {
      console.error("❌ Error scheduling interview:", err);
      return {
        success: false,
        error: err.message || "Failed to schedule interview",
      };
    }
  };

  // Job alerts management
  const triggerJobAlerts = async (
    jobId: string,
  ): Promise<{ success: boolean; sent?: number; error?: string }> => {
    try {
      console.log("🔔 Triggering job alerts for job:", jobId);

      const result = await apiRequest<{ alerts: { sent: number } }>(
        `/jobs/${jobId}/trigger-alerts`,
        { method: "POST" },
      );

      console.log("✅ Job alerts triggered successfully");
      return {
        success: true,
        sent: result.alerts?.sent || 0,
      };
    } catch (err: any) {
      console.error("❌ Error triggering job alerts:", err);
      return {
        success: false,
        error: err.message || "Failed to trigger job alerts",
      };
    }
  };

  const triggerBulkJobAlerts = async (): Promise<{
    success: boolean;
    totalSent?: number;
    totalFailed?: number;
    error?: string;
  }> => {
    try {
      console.log("🔔 Triggering bulk job alerts");

      const jobsData = await fetchJobs();
      const activeJobs = jobsData.jobs.filter(
        (job) => !job.deadline || new Date(job.deadline) > new Date(),
      );

      let totalSent = 0;
      let totalFailed = 0;

      for (const job of activeJobs) {
        try {
          const result = await triggerJobAlerts(job.id);
          if (result.success && result.sent) {
            totalSent += result.sent;
          } else {
            totalFailed++;
          }
        } catch (error) {
          totalFailed++;
          console.error(`Failed to trigger alerts for job ${job.id}:`, error);
        }
      }

      console.log("✅ Bulk job alerts completed");
      return {
        success: true,
        totalSent,
        totalFailed,
      };
    } catch (err: any) {
      console.error("❌ Error in bulk job alerts:", err);
      return {
        success: false,
        error: err.message || "Failed to trigger bulk job alerts",
      };
    }
  };

  // Job management
  const fetchJobs = async (): Promise<{ jobs: Job[] }> => {
    try {
      console.log("💼 Fetching jobs...");
      const data = await apiRequest<any>("/admin/jobs");
      console.log("✅ Jobs fetched:", data.jobs?.length || data.length);

      // Ensure consistent return format
      return {
        jobs: Array.isArray(data) ? data : data.jobs || [],
      };
    } catch (err: any) {
      console.error("❌ Error fetching jobs:", err);
      throw new Error(err.message || "Failed to fetch jobs");
    }
  };
  const deleteJob = async (
    jobId: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log("🗑️ Deleting job:", jobId);
      await apiRequest(`/admin/jobs/${jobId}`, { method: "DELETE" });
      console.log("✅ Job deleted successfully");
      return { success: true };
    } catch (err: any) {
      console.error("❌ Error deleting job:", err);
      return { success: false, error: err.message || "Failed to delete job" };
    }
  };

  // User management
  const fetchUsers = async (): Promise<User[]> => {
    try {
      console.log("👥 Fetching users...");
      const data = await apiRequest<any>("/admin/users");
      console.log("✅ Users fetched:", data.users?.length || data.length);

      // Handle both response formats
      if (Array.isArray(data)) {
        return data;
      } else if (data.users && Array.isArray(data.users)) {
        return data.users;
      } else {
        console.warn("Unexpected users response format:", data);
        return [];
      }
    } catch (err: any) {
      console.error("❌ Error fetching users:", err);
      throw new Error(err.message || "Failed to fetch users");
    }
  };

  const updateUserRole = async (
    userId: string,
    newRole: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log("👑 Updating user role:", { userId, newRole });
      await apiRequest(`/admin/users/${userId}`, {
        method: "PATCH",
        body: JSON.stringify({ role: newRole }),
      });
      console.log("✅ User role updated successfully");
      return { success: true };
    } catch (err: any) {
      console.error("❌ Error updating user role:", err);
      return {
        success: false,
        error: err.message || "Failed to update user role",
      };
    }
  };

  const activateUser = async (
    userId: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log("✅ Activating user:", userId);
      await apiRequest(`/auth/admin/users/${userId}/reactivate`, {
        method: "PATCH",
      });
      console.log("✅ User activated successfully");
      return { success: true };
    } catch (err: any) {
      console.error("❌ Error activating user:", err);
      return {
        success: false,
        error: err.message || "Failed to activate user",
      };
    }
  };

  const deactivateUser = async (
    userId: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log("❌ Deactivating user:", userId);
      await apiRequest(`/auth/admin/users/${userId}/deactivate`, {
        method: "PATCH",
      });
      console.log("✅ User deactivated successfully");
      return { success: true };
    } catch (err: any) {
      console.error("❌ Error deactivating user:", err);
      return {
        success: false,
        error: err.message || "Failed to deactivate user",
      };
    }
  };

  const deleteUser = async (
    userId: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log("🗑️ Deleting user:", userId);
      await apiRequest(`/admin/users/${userId}`, { method: "DELETE" });
      console.log("✅ User deleted successfully");
      return { success: true };
    } catch (err: any) {
      console.error("❌ Error deleting user:", err);
      return { success: false, error: err.message || "Failed to delete user" };
    }
  };

  // Job alerts management
  const fetchJobAlerts = async (): Promise<{ alerts: JobAlert[] }> => {
    try {
      console.log("🔔 Fetching job alerts...");
      const data = await apiRequest<any>("/admin/job-alerts");
      console.log("✅ Job alerts fetched:", data.alerts?.length || data.length);

      return {
        alerts: Array.isArray(data) ? data : data.alerts || [],
      };
    } catch (err: any) {
      console.error("❌ Error fetching job alerts:", err);
      throw new Error(err.message || "Failed to fetch job alerts");
    }
  };

  const updateJobAlertStatus = async (
    alertId: string,
    isActive: boolean,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log("🔧 Updating job alert status:", { alertId, isActive });
      await apiRequest(`/jobs/alerts/${alertId}`, {
        method: "PUT",
        body: JSON.stringify({ isActive }),
      });
      console.log("✅ Job alert status updated successfully");
      return { success: true };
    } catch (err: any) {
      console.error("❌ Error updating job alert status:", err);
      return {
        success: false,
        error: err.message || "Failed to update job alert status",
      };
    }
  };

  const deleteJobAlert = async (
    alertId: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log("🗑️ Deleting job alert:", alertId);
      await apiRequest(`/jobs/alerts/${alertId}`, { method: "DELETE" });
      console.log("✅ Job alert deleted successfully");
      return { success: true };
    } catch (err: any) {
      console.error("❌ Error deleting job alert:", err);
      return {
        success: false,
        error: err.message || "Failed to delete job alert",
      };
    }
  };

  // Dashboard stats
  const fetchApplicationStats = async (): Promise<any> => {
    try {
      console.log("📊 Fetching application stats...");
      const data = await apiRequest<{ stats: any }>(
        "/admin/applications/stats/overview",
      );
      console.log("✅ Application stats fetched");
      return data.stats;
    } catch (err: any) {
      console.error("❌ Error fetching application stats:", err);
      throw new Error(err.message || "Failed to fetch application stats");
    }
  };

  const calculateDashboardStats = (
    users: User[],
    jobs: Job[],
    applications: Application[],
    alerts: JobAlert[],
  ): DashboardStats => {
    const isJobExpiringSoon = (
      deadline: string,
      daysThreshold: number = 3,
    ): boolean => {
      if (!deadline) return false;
      const now = new Date();
      const deadlineDate = new Date(deadline);
      const timeDiff = deadlineDate.getTime() - now.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
      return daysDiff <= daysThreshold && daysDiff >= 0;
    };

    const expiringSoonJobs = jobs.filter(
      (job) => job.deadline && isJobExpiringSoon(job.deadline),
    ).length;

    const stats: DashboardStats = {
      totalUsers: users.length,
      totalJobs: jobs.length,
      totalApplications: applications.length,
      totalAlerts: alerts.length,
      pendingApplications: applications.filter(
        (app) => app.status === "pending",
      ).length,
      activeUsers: users.filter((user) => user.active).length,
      expiredJobs: jobs.filter(
        (job) => job.deadline && new Date(job.deadline) < new Date(),
      ).length,
      activeAlerts: alerts.filter((alert) => alert.isActive).length,
      expiringSoonJobs: expiringSoonJobs,
    };

    return stats;
  };

  // Initialize data based on user role
  useEffect(() => {
    console.log("🔄 ApplicationProvider initializing with user:", user?.email);

    if (user) {
      fetchApplications();
      if (user.role === "admin") {
        fetchPackages();
        fetchPackageStats();
      }
    } else {
      console.log("ℹ️ No user found, clearing application data");
      setApplications([]);
      setPackages([]);
      setPackageStats(null);
    }
  }, [user, fetchApplications, fetchPackages, fetchPackageStats]);

  // Initialize WhatsApp status if admin
  useEffect(() => {
    if (user?.role === "admin") {
      getWhatsAppStatus().catch(console.error);
    }
  }, [user]);

  const value: ApplicationContextType = {
    // Application management
    applications,
    loading,
    error,
    refreshApplications: fetchApplications,
    addApplication,
    updateApplication,
    removeApplication,
    hasAppliedToJob,
    getApplicationByJobId,
    getApplicationsByStatus,
    submitApplication,
    withdrawApplication,
    updateApplicationStatus: updateApplicationStatusUser,

    // WhatsApp properties
    whatsappStatus,
    whatsappLoading,
    whatsappError,

    // Update contactApplicant with new signature
    contactApplicant,

    // WhatsApp methods
    getWhatsAppStatus,
    sendWhatsAppMessage,
    sendBulkWhatsAppMessages,
    disconnectWhatsApp,
    reconnectWhatsApp,

    // Package management
    packages,
    packageStats,
    packageLoading,
    packageError,
    packageSuccessMessage,
    refreshPackages: fetchPackages,
    refreshPackageStats: fetchPackageStats,
    createPackage,
    updatePackage,
    deletePackage,
    deactivatePackage,
    togglePackageStatus,
    togglePopularStatus,
    restorePackage,
    getSubscriptionCount,

    // Admin management functions
    fetchAllApplications,
    deleteApplication,
    deleteMultipleApplications,
    // contactApplicant,
    scheduleInterview,
    triggerJobAlerts,
    triggerBulkJobAlerts,
    fetchJobs,
    deleteJob,
    fetchUsers,
    updateUserRole,
    activateUser,
    deactivateUser,
    deleteUser,
    fetchJobAlerts,
    updateJobAlertStatus,
    deleteJobAlert,
    fetchApplicationStats,
    calculateDashboardStats,
  };

  return (
    <ApplicationContext.Provider value={value}>
      {children}
    </ApplicationContext.Provider>
  );
};

// ====================== CUSTOM HOOKS ======================

export const useApplication = (): ApplicationContextType => {
  const context = useContext(ApplicationContext);
  if (context === undefined) {
    throw new Error(
      "useApplication must be used within an ApplicationProvider",
    );
  }
  return context;
};

// Custom hook for package management (admin only)
export const usePackageManagement = () => {
  const context = useContext(ApplicationContext);
  if (context === undefined) {
    throw new Error(
      "usePackageManagement must be used within an ApplicationProvider",
    );
  }

  const { user } = useAuth();

  if (!user || user.role !== "admin") {
    throw new Error("Package management requires admin privileges");
  }

  return {
    packages: context.packages,
    packageStats: context.packageStats,
    packageLoading: context.packageLoading,
    packageError: context.packageError,
    packageSuccessMessage: context.packageSuccessMessage,
    refreshPackages: context.refreshPackages,
    refreshPackageStats: context.refreshPackageStats,
    createPackage: context.createPackage,
    updatePackage: context.updatePackage,
    deletePackage: context.deletePackage,
    deactivatePackage: context.deactivatePackage,
    togglePackageStatus: context.togglePackageStatus,
    togglePopularStatus: context.togglePopularStatus,
    restorePackage: context.restorePackage,
    getSubscriptionCount: context.getSubscriptionCount,
  };
};

// Enhanced Admin-specific hook for application management
export const useAdminApplications = () => {
  const context = useContext(ApplicationContext);
  const { user } = useAuth();

  if (context === undefined) {
    throw new Error(
      "useAdminApplications must be used within an ApplicationProvider",
    );
  }

  if (!user || user.role !== "admin") {
    throw new Error("Admin access required");
  }

  return {
    // From ApplicationContext
    ...context,

    // Aliases for consistency and backward compatibility
    fetchAllApplications: context.fetchAllApplications,
    updateApplicationStatus: context.updateApplicationStatus,
    deleteApplication: context.deleteApplication,
    deleteMultipleApplications: context.deleteMultipleApplications,
    contactApplicant: context.contactApplicant,
    scheduleInterview: context.scheduleInterview,
    triggerJobAlerts: context.triggerJobAlerts,
    triggerBulkJobAlerts: context.triggerBulkJobAlerts,
    fetchJobs: context.fetchJobs,
    deleteJob: context.deleteJob,
    fetchUsers: context.fetchUsers,
    updateUserRole: context.updateUserRole,
    activateUser: context.activateUser,
    deactivateUser: context.deactivateUser,
    deleteUser: context.deleteUser,
    fetchJobAlerts: context.fetchJobAlerts,
    updateJobAlertStatus: context.updateJobAlertStatus,
    deleteJobAlert: context.deleteJobAlert,
    fetchApplicationStats: context.fetchApplicationStats,
    calculateDashboardStats: context.calculateDashboardStats,
  };
};
